import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  Tooltip,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Checkbox,
  ListItemText,
  Autocomplete,
  Menu,
  ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Visibility as VisibilityIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Refresh as RefreshIcon,
  Login as LoginIcon,
  LockReset as LockResetIcon,
  FileCopy as FileCopyIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Channel picker is hidden while Org == Channel for effectively every org (the parent-company
// tier was never populated; only Dreams→Clouds diverges). Flip to true to re-expose the picker
// when parent orgs become real — the grant plumbing underneath is fully wired and untouched.
const SHOW_CHANNEL_PICKER = false;

const UserManagement = () => {
  const navigate = useNavigate();
  
  // State management
  const [users, setUsers] = useState([]);
  const [allParentUsers, setAllParentUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [copyUserDialogOpen, setCopyUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'merchant',
    parentUserId: null,
    allowedTerminals: [],
    allowedLocations: [],
    allowedAccounts: [],
    allowedOrganizations: [],
    allowedChannels: [],
    allowedMarkets: [],
    canManageUsers: false,
    temporaryPassword: ''
  });

  // Notification states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const { getUsers, getUserById, createUser, updateUser, deleteUser, resetUserPassword, copyUser, getLocations, getAccounts, getUserChannelGrants, setUserChannelGrants, getUserMarketGrants, setUserMarketGrants } = useApi();
  const { userProfile: currentUser, refreshProfile, getIdToken, user } = useAuth();

  // Delegated user management: admins get everything; a "manager" is a
  // non-admin whose users.can_manage_users flag is set (AuthContext probe).
  // Managers only see their own subtree and may only assign resources within
  // their own scope — the backend enforces all of it; these flags shape the UI.
  const isAdmin = currentUser?.role === 'admin';
  const isManager = isAdmin || currentUser?.canManageUsers === true;
  // Managers may only act on merchant/subagent rows, never their own row
  // (the backend enforces the same; this just hides dead-end menu items)
  const canMutateSelected = isAdmin || (
    selectedUser &&
    ['merchant', 'subagent'].includes(selectedUser.role) &&
    selectedUser.cognito_user_id !== currentUser?.userId
  );

  // Location state - must be before any conditional returns
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Accounts state - must be before any conditional returns
  const [accounts, setAccounts] = useState([]);
  // Stores a delegated manager may assign (from managerScope) — includes partner-group stores
  const [scopeBusinesses, setScopeBusinesses] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Organizations state - for org-based permission assignment
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  // Channels/Markets state - for channel and market-based permission assignment
  const [channels, setChannels] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);

  // Mock data state - updated when real API calls succeed
  const [mockUsers, setMockUsers] = useState([
    {
      id: 1,
      first_name: 'Admin',
      last_name: 'User',
      email: 'admin@example.com',
      role: 'admin',
      status: 'active',
      parent_name: null,
      child_count: 2,
      allowed_locations: [], // Admin has access to all
      created_at: new Date().toISOString()
    },
    {
      id: 2,
      first_name: 'Manager',
      last_name: 'User',
      email: 'agent@example.com',
      role: 'agent',
      status: 'active',
      parent_name: 'Admin User',
      child_count: 1,
      allowed_locations: ['Location A - New York', 'Location B - Los Angeles'],
      created_at: new Date().toISOString()
    },
    {
      id: '4408c448-a061-70ee-1b45-9c20bba60952',
      first_name: 'John',
      last_name: 'Merchant',
      email: 'merchant1@example.com',
      role: 'merchant',
      status: 'active',
      parent_name: 'Admin User',
      child_count: 0,
      allowed_locations: ['Location A - New York'],
      created_at: new Date().toISOString()
    },
    {
      id: '941844f8-10c1-70db-4a22-18f96f836c8f',
      first_name: 'Jane',
      last_name: 'Agent',
      email: 'agent1@example.com',
      role: 'agent',
      status: 'active',
      parent_name: 'Admin User',
      child_count: 0,
      allowed_locations: ['Location A - New York', 'Location B - Los Angeles'],
      created_at: new Date().toISOString()
    },
    {
      id: 'f4186488-d051-7000-a83e-4ac064d00ea8',
      first_name: 'Bob',
      last_name: 'SubAgent',
      email: 'subagent1@example.com',
      role: 'subagent',
      status: 'active',
      parent_name: 'Jane Agent',
      child_count: 0,
      allowed_locations: ['Location A - New York'],
      created_at: new Date().toISOString()
    }
  ]);

  // Load users
  const loadUsers = useCallback(async () => {
    if (!user) return; // Don't load if not authenticated
    
    setLoading(true);
    try {
      const params = {
        page: page + 1, // API expects 1-based pagination
        limit: rowsPerPage,
        search: searchTerm,
        role: roleFilter,
        status: statusFilter
      };
      // Managers: ask for a fresh assignable-scope snapshot so the Channel/
      // Market pickers don't rely solely on the login-probe copy (which goes
      // stale if an admin widens/narrows the manager's grants mid-session)
      if (currentUser && currentUser.role !== 'admin' && currentUser.canManageUsers) {
        params.includeScope = 1;
      }

      const response = await getUsers(params);
      setUsers(response.users || []);
      setTotalUsers(response.pagination?.total || 0);
      if (response.managerScope) {
        setOrganizations((response.managerScope.organizations || []).slice().sort().map(name => ({ name })));
        setMarkets((response.managerScope.markets || []).slice().sort());
        setScopeBusinesses((response.managerScope.businesses || []).slice().sort());
      }
    } catch (error) {
      showSnackbar('Error loading users: ' + error.message, 'error');
      // Fallback to mock data on error
      setUsers(mockUsers);
      setTotalUsers(mockUsers.length);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, roleFilter, statusFilter, getUsers, mockUsers, user, currentUser]);

  // Fetch all potential parent users
  const loadParentUsers = useCallback(async () => {
    if (!user) return;
    
    try {
      // Fetch all admin, agent, and subagent users for parent selection
      const response = await getUsers({
        limit: 1000, // Get all users
        status: 'active' // Only active users can be parents
      });
      
      const parentUsers = response.users?.filter(u => 
        u.role === 'admin' || u.role === 'agent' || u.role === 'subagent'
      ) || [];
      
      setAllParentUsers(parentUsers);
    } catch (error) {
      console.error('Error loading parent users:', error);
      // Use mock data fallback
      setAllParentUsers(mockUsers.filter(u => 
        u.role === 'admin' || u.role === 'agent' || u.role === 'subagent'
      ));
    }
  }, [user, getUsers, mockUsers]);


  // Load locations for assignment
  const loadLocations = useCallback(async () => {
    if (!user) return; // Don't load if not authenticated
    
    setLoadingLocations(true);
    try {
      const response = await getLocations();
      setLocations(response || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      // Set some mock locations as fallback
      setLocations([
        { LocationName: 'Location A - New York', TerminalIDs: ['T001', 'T002'] },
        { LocationName: 'Location B - Los Angeles', TerminalIDs: ['T003', 'T004'] },
        { LocationName: 'Location C - Chicago', TerminalIDs: ['T005', 'T006'] },
        { LocationName: 'Restaurant Downtown', TerminalIDs: ['T007'] },
        { LocationName: 'Retail Store Mall', TerminalIDs: ['T008', 'T009', 'T010'] }
      ]);
    } finally {
      setLoadingLocations(false);
    }
  }, [user, getLocations]);

  // Load accounts for assignment
  const loadAccounts = useCallback(async () => {
    if (!user) return; // Don't load if not authenticated

    setLoadingAccounts(true);
    try {
      const response = await getAccounts();
      setAccounts(response || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
      // No fallback list: never ship real account data as a hardcoded default (BL-19),
      // and a fabricated list would let assignments reference accounts that don't exist.
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, [user, getAccounts]);

  // Load organizations for assignment
  const loadOrganizations = useCallback(async () => {
    if (!user) return; // Don't load if not authenticated

    setLoadingOrganizations(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/channels/organizations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load organizations');
      }

      const data = await response.json();
      setOrganizations(data.organizations || []);
    } catch (error) {
      console.error('Error loading organizations:', error);
      // Set empty array as fallback
      setOrganizations([]);
    } finally {
      setLoadingOrganizations(false);
    }
  }, [user, getIdToken]);

  // Load channels and derive unique markets from /api/v1/channels
  // The channels endpoint returns { channels: [{ name, organization, store_count, terminal_count }] }
  // where "name" is the market value and "organization" is the channel/org value.
  const loadChannels = useCallback(async () => {
    if (!user) return;

    setLoadingChannels(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/channels`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load channels');
      }

      const data = await response.json();
      const channelList = data.channels || [];
      setChannels(channelList);

      // Derive unique market values from the "name" field of each channel record
      const uniqueMarkets = [...new Set(channelList.map(c => c.name).filter(Boolean))].sort();
      setMarkets(uniqueMarkets);
    } catch (error) {
      console.error('Error loading channels:', error);
      setChannels([]);
      setMarkets([]);
    } finally {
      setLoadingChannels(false);
    }
  }, [user, getIdToken]);

  // Derive distinct channel options from the "organization" field of the channels data.
  // Each channels row has name=market and organization=channel; Channel multi-select
  // must show unique organization values so the grant payload carries the correct value.
  const distinctChannels = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const ch of channels) {
      if (ch.organization && !seen.has(ch.organization)) {
        seen.add(ch.organization);
        result.push({ name: ch.organization });
      }
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [channels]);

  // Load data on mount
  useEffect(() => {
    // Load for admins and delegated managers (backend scopes everything server-side)
    if (user && currentUser && isManager) {
      loadUsers();
      loadParentUsers();
      loadLocations();   // /businesses — already server-filtered per requester
      loadAccounts();    // /accounts  — already server-filtered per requester
      if (isAdmin) {
        // /channels* endpoints are admin-only — managers would 403
        loadOrganizations();
        loadChannels();
      } else {
        // Managers: Channel/Market picker options come from their own scope,
        // captured off the getUsers response by the AuthContext login probe
        const scope = currentUser.managerScope || {};
        setOrganizations((scope.organizations || []).slice().sort().map(name => ({ name })));
        setMarkets((scope.markets || []).slice().sort());
        setScopeBusinesses(prev => prev.length ? prev : (scope.businesses || []).slice().sort());
        setChannels([]);
      }
    }
  }, [loadUsers, loadParentUsers, user, currentUser, isManager, isAdmin, loadLocations, loadAccounts, loadOrganizations, loadChannels]);

  // Helper functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: 'merchant',
      parentUserId: null,
      allowedTerminals: [],
      allowedLocations: [],
      allowedAccounts: [],
      allowedOrganizations: [],
      allowedChannels: [],
      allowedMarkets: [],
      canManageUsers: false,
      temporaryPassword: ''
    });
  };

  const getAvailableParentUsers = () => {
    if (!formData.role) return [];

    let filteredUsers = [];

    // For SubAgents, show Admin and Agent users
    if (formData.role === 'subagent') {
      filteredUsers = allParentUsers.filter(user =>
        user.role === 'admin' || user.role === 'agent'
      );
    }

    // For Merchants, show Admin, Agent, and SubAgent users
    if (formData.role === 'merchant') {
      filteredUsers = allParentUsers.filter(user =>
        user.role === 'admin' || user.role === 'agent' || user.role === 'subagent'
      );
    }

    // Sort alphabetically by first name, then last name
    return filteredUsers.sort((a, b) => {
      const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
      const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <AdminPanelSettingsIcon color="error" />;
      case 'agent':
        return <SupervisorAccountIcon color="warning" />;
      case 'subagent':
        return <AnalyticsIcon color="info" />;
      case 'merchant':
        return <VisibilityIcon color="action" />;
      default:
        return <PersonIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'agent':
        return 'warning';
      case 'subagent':
        return 'info';
      case 'merchant':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // CRUD operations

  // Cognito password policy for this pool: 8+ chars with an uppercase, lowercase,
  // number, and symbol. Validate client-side so a non-conforming password (or a
  // malformed email) is caught BEFORE the Cognito round-trip — otherwise Cognito
  // validates the email first and a missing symbol surfaces as an opaque 500.
  const getPasswordErrors = (pw) => {
    const p = pw || '';
    const errs = [];
    if (p.length < 8) errs.push('8+ characters');
    if (!/[A-Z]/.test(p)) errs.push('an uppercase letter');
    if (!/[a-z]/.test(p)) errs.push('a lowercase letter');
    if (!/[0-9]/.test(p)) errs.push('a number');
    if (!/[^A-Za-z0-9]/.test(p)) errs.push('a symbol');
    return errs;
  };
  const isEmailish = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

  const handleCreateUser = async () => {
    try {
      const cleanEmail = (formData.email || '').trim();
      if (!isEmailish(cleanEmail)) {
        showSnackbar('Please enter a valid email address (e.g. name@company.com).', 'error');
        return;
      }
      // A blank password is fine — the backend auto-generates a compliant one.
      if (formData.temporaryPassword) {
        const pwErrs = getPasswordErrors(formData.temporaryPassword);
        if (pwErrs.length > 0) {
          showSnackbar(`Temporary password needs ${pwErrs.join(', ')} — or leave it blank to auto-generate one.`, 'error');
          return;
        }
      }
      const userData = {
        email: cleanEmail,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        parentUserId: formData.parentUserId,
        allowedTerminals: formData.allowedTerminals,
        allowedBusinesses: formData.allowedLocations,
        allowedAccounts: isAdmin ? formData.allowedAccounts : [],
        allowedOrganizations: formData.allowedOrganizations,
        canManageUsers: isAdmin ? formData.canManageUsers : false,
        temporaryPassword: formData.temporaryPassword
      };
      
      console.log('Creating user with data:', userData);
      const response = await createUser(userData);
      
      // If a password was generated, show it to the admin
      if (response.temporaryPassword) {
        setResetPasswordResult({
          success: true,
          temporaryPassword: response.temporaryPassword,
          email: formData.email
        });
        showSnackbar('User created successfully', 'success');
      } else {
        showSnackbar('User created successfully');
      }
      
      // Add new user to mock data for immediate display
      const newUser = {
        id: Date.now(), // Use timestamp as temporary ID
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        role: formData.role,
        status: 'active',
        parent_name: formData.parentUserId ? 
          mockUsers.find(u => u.id === formData.parentUserId)?.first_name + ' ' + 
          mockUsers.find(u => u.id === formData.parentUserId)?.last_name : null,
        child_count: 0,
        allowed_locations: formData.allowedLocations,
        created_at: new Date().toISOString()
      };
      
      console.log('New user to add to mock:', newUser);
      setMockUsers(prev => [...prev, newUser]);
      
      setCreateDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (error) {
      // Check for specific error messages
      let errorMessage = 'Error creating user: ';
      if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      // Don't add to mock data if creation failed
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditUser = async () => {
    try {
      // Only send fields that have actually changed
      const userData = {};

      // Check if email changed
      if (formData.email !== selectedUser.email) {
        userData.email = formData.email;
      }

      if (formData.firstName !== selectedUser.first_name) {
        userData.firstName = formData.firstName;
      }
      if (formData.lastName !== selectedUser.last_name) {
        userData.lastName = formData.lastName;
      }
      if (formData.role !== selectedUser.role) {
        userData.role = formData.role;
      }
      if (formData.parentUserId !== selectedUser.parent_user_id) {
        userData.parentUserId = formData.parentUserId;
      }

      // Check if ANY permissions have changed
      const terminalsChanged = JSON.stringify(formData.allowedTerminals?.sort()) !==
                               JSON.stringify(selectedUser.allowed_terminals?.sort());

      const formLocations = formData.allowedLocations || [];
      const userLocations = selectedUser.allowed_locations || [];
      const locationsChanged = JSON.stringify(formLocations.sort()) !==
                               JSON.stringify(userLocations.sort());

      const accountsChanged = JSON.stringify(formData.allowedAccounts?.sort()) !==
                             JSON.stringify(selectedUser.allowed_accounts?.sort());

      const organizationsChanged = JSON.stringify(formData.allowedOrganizations?.sort()) !==
                                   JSON.stringify(selectedUser.allowed_organizations?.sort());

      // If ANY permission changed, send ALL permissions to prevent data loss
      // The backend clears all permissions and re-inserts, so we must send all types
      if (terminalsChanged || locationsChanged || accountsChanged || organizationsChanged) {
        userData.allowedTerminals = formData.allowedTerminals || [];
        userData.allowedBusinesses = formData.allowedLocations || [];
        userData.allowedAccounts = formData.allowedAccounts || [];
        userData.allowedOrganizations = formData.allowedOrganizations || [];
        console.log('Permissions changed, sending all permission types to prevent data loss');
      }

      if (isAdmin && formData.canManageUsers !== selectedUser.can_manage_users) {
        userData.canManageUsers = formData.canManageUsers;
      }

      console.log('Updating user with data:', userData);
      const response = await updateUser(selectedUser.id, userData);

      // Apply Channel and Market grants via dedicated endpoints (diff-based, independent error handling)
      const grantErrors = [];
      try {
        await setUserChannelGrants(selectedUser.id, formData.allowedChannels || []);
      } catch (err) {
        grantErrors.push(`Channel: ${err.message}`);
      }
      try {
        await setUserMarketGrants(selectedUser.id, formData.allowedMarkets || []);
      } catch (err) {
        grantErrors.push(`Market: ${err.message}`);
      }
      if (grantErrors.length > 0) {
        setSnackbar({
          open: true,
          message: `User saved, but some grants had errors: ${grantErrors.join('; ')}`,
          severity: 'warning'
        });
        await loadUsers();
        setEditDialogOpen(false);
        resetForm();
        setSelectedUser(null);
        return;
      }

      // If email was changed, show the new temporary password
      if (response.emailChanged) {
        setResetPasswordResult({
          success: true,
          temporaryPassword: response.temporaryPassword,
          email: response.newEmail
        });
        showSnackbar('User updated - email changed. New password required.', 'success');
        setResetPasswordDialogOpen(true);
      } else {
        showSnackbar('User updated successfully');
      }

      // Reload users to get fresh data
      loadUsers();

      // Update user in mock data
      const updatedMockUsers = prev => prev.map(user =>
        user.id === selectedUser.id
          ? { ...user,
              email: formData.email,
              first_name: formData.firstName,
              last_name: formData.lastName,
              role: formData.role,
              parent_name: formData.parentUserId ?
                mockUsers.find(u => u.id === formData.parentUserId)?.first_name + ' ' +
                mockUsers.find(u => u.id === formData.parentUserId)?.last_name : null,
              allowed_locations: formData.allowedLocations
            }
          : user
      );

      console.log('Form data allowed locations:', formData.allowedLocations);
      setMockUsers(updatedMockUsers);

      setEditDialogOpen(false);
      resetForm();
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      showSnackbar('Error updating user: ' + error.message, 'error');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(selectedUser.id);
      showSnackbar('User deleted successfully');
      
      // Remove user from mock data
      setMockUsers(prev => prev.filter(user => user.id !== selectedUser.id));
      
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      showSnackbar('Error deleting user: ' + error.message, 'error');
    }
  };

  const handleSuspendUser = async (userId, suspend = true) => {
    try {
      const userData = {
        status: suspend ? 'suspended' : 'active'
      };
      
      await updateUser(userId, userData);
      showSnackbar(`User ${suspend ? 'suspended' : 'activated'} successfully`);
      
      // Update user status in mock data
      setMockUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, status: suspend ? 'suspended' : 'active' }
          : user
      ));
      
      loadUsers();
    } catch (error) {
      showSnackbar(`Error ${suspend ? 'suspending' : 'activating'} user: ` + error.message, 'error');
    }
  };

  // Event handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateClick = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const handleEditClick = async (user) => {
    try {
      console.log('Fetching full user data for:', user.email);
      // Fetch full user data and current Channel/Market grants in parallel
      const [fullUserData, channelGrants, marketGrants] = await Promise.all([
        getUserById(user.id),
        getUserChannelGrants(user.id).catch(err => {
          if (err.status === 404 || /not found/i.test(err.message || '')) {
            return [];
          }
          console.error('Failed to load Channel grants:', err);
          return [];
        }),
        getUserMarketGrants(user.id).catch(err => {
          if (err.status === 404 || /not found/i.test(err.message || '')) {
            return [];
          }
          console.error('Failed to load Market grants:', err);
          return [];
        })
      ]);
      console.log('Full user data from API:', fullUserData);
      console.log('User allowed locations count:', fullUserData.allowed_businesses?.length || 0);
      console.log('Channel grants:', channelGrants, 'Market grants:', marketGrants);

      setSelectedUser(fullUserData);
      const formDataToSet = {
        email: fullUserData.email,
        firstName: fullUserData.first_name,
        lastName: fullUserData.last_name,
        role: fullUserData.role,
        parentUserId: fullUserData.parent_user_id,
        allowedTerminals: fullUserData.allowed_terminals || [],
        allowedLocations: fullUserData.allowed_businesses || [], // Use allowed_businesses from API
        allowedAccounts: fullUserData.allowed_accounts || [],
        allowedOrganizations: fullUserData.allowed_organizations || [],
        allowedChannels: channelGrants.map(p => p.channel),
        allowedMarkets: marketGrants.map(p => p.market),
        canManageUsers: fullUserData.can_manage_users,
        temporaryPassword: ''
      };
      console.log('Setting form data with full permissions:', formDataToSet);
      setFormData(formDataToSet);
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error fetching user data:', error);
      showSnackbar('Failed to load user data', 'error');
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleImpersonateClick = (user) => {
    setSelectedUser(user);
    setImpersonateDialogOpen(true);
  };

  const handleResetPasswordClick = (user) => {
    setSelectedUser(user);
    setResetPasswordResult(null);
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    try {
      const response = await resetUserPassword(selectedUser.id);
      setResetPasswordResult({
        success: true,
        temporaryPassword: response.temporaryPassword,
        email: response.email
      });
      showSnackbar(`Password reset successfully for ${selectedUser.first_name} ${selectedUser.last_name}`, 'success');
    } catch (error) {
      setResetPasswordResult({
        success: false,
        error: error.message
      });
      showSnackbar('Error resetting password: ' + error.message, 'error');
    }
  };

  const handleCopyUserClick = (user) => {
    setSelectedUser(user);
    // Pre-fill with source user's name but allow editing
    setFormData({
      email: '',
      firstName: user.first_name,
      lastName: user.last_name,
      role: user.role,
      parentUserId: user.parent_user_id,
      allowedTerminals: user.allowed_terminals || [],
      allowedLocations: user.allowed_locations || [],
      allowedAccounts: user.allowed_accounts || [],
      allowedOrganizations: user.allowed_organizations || [],
      allowedChannels: [],
      allowedMarkets: [],
      canManageUsers: user.can_manage_users,
      temporaryPassword: ''
    });
    setCopyUserDialogOpen(true);
  };

  const handleCopyUser = async () => {
    try {
      const copyData = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        temporaryPassword: formData.temporaryPassword
      };

      console.log('Copying user with data:', copyData);
      const response = await copyUser(selectedUser.id, copyData);

      // Show generated password
      if (response.temporaryPassword) {
        setResetPasswordResult({
          success: true,
          temporaryPassword: response.temporaryPassword,
          email: formData.email
        });
        showSnackbar(`User copied successfully from ${response.copiedFrom.name}`, 'success');
      } else {
        showSnackbar('User copied successfully');
      }

      setCopyUserDialogOpen(false);
      resetForm();
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      showSnackbar('Error copying user: ' + error.message, 'error');
    }
  };

  const handleImpersonateUser = async () => {
    try {
      // Store original user info before impersonation
      const originalUser = {
        userId: currentUser.userId,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        role: currentUser.role
      };
      
      // Store in session storage so we can restore later
      sessionStorage.setItem('originalUser', JSON.stringify(originalUser));
      sessionStorage.setItem('isImpersonating', 'true');
      
      // Get the ID token for API call (authorizer requires ID token, not access token)
      const idToken = await getIdToken();

      // Create impersonation token for the target user
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/admin/impersonate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          targetUserId: selectedUser.id || selectedUser.email
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create impersonation token');
      }

      const impersonationData = await response.json();
      
      // Store impersonation token and target user info
      sessionStorage.setItem('impersonationToken', impersonationData.token);
      sessionStorage.setItem('impersonatedUser', JSON.stringify({
        userId: impersonationData.targetUser.userId,
        email: impersonationData.targetUser.email,
        firstName: impersonationData.targetUser.firstName,
        lastName: impersonationData.targetUser.lastName,
        role: impersonationData.targetUser.role,
        allowedTerminals: impersonationData.targetUser.allowedTerminals || [],
        allowedBusinesses: impersonationData.targetUser.allowedBusinesses || [],
        allowedLocations: impersonationData.targetUser.allowedBusinesses || [], // Map businesses to locations for frontend compatibility
        canManageUsers: impersonationData.targetUser.canManageUsers === true, // reflect delegated-mgmt while impersonating
        partnerGroups: impersonationData.targetUser.partnerGroups || [] // Partner Surcharge menu while impersonating
      }));
      
      showSnackbar(`Now impersonating ${selectedUser.first_name} ${selectedUser.last_name}`, 'info');
      setImpersonateDialogOpen(false);
      setSelectedUser(null);
      
      // Refresh the user profile to reflect the impersonation
      if (refreshProfile) {
        await refreshProfile();
      }
      
      // Redirect to overview page (merchants/agents can't access user management)
      window.location.href = '/overview';
      
    } catch (error) {
      showSnackbar('Error impersonating user: ' + error.message, 'error');
    }
  };

  const handleStopImpersonation = async () => {
    try {
      // Clear impersonation data
      sessionStorage.removeItem('impersonationToken');
      sessionStorage.removeItem('impersonatedUser');
      sessionStorage.removeItem('originalUser');
      sessionStorage.removeItem('isImpersonating');
      
      showSnackbar('Stopped impersonation, returned to original account', 'success');
      
      // Refresh the user profile to restore original user
      if (refreshProfile) {
        await refreshProfile();
      }
      
      // Reload the page to ensure all components update
      window.location.reload();
      
    } catch (error) {
      showSnackbar('Error stopping impersonation: ' + error.message, 'error');
    }
  };

  // Check if currently impersonating
  const isImpersonating = sessionStorage.getItem('isImpersonating') === 'true';
  const originalUser = isImpersonating ? JSON.parse(sessionStorage.getItem('originalUser') || '{}') : null;

  // Render user form dialog
  const renderUserFormDialog = (open, onClose, onSubmit, title, submitText) => {
    // Managers can assign stores beyond their own /businesses data view (e.g. partner-group
    // stores) — merge managerScope.businesses into the picker options.
    const locationOptions = isAdmin ? locations : [
      ...locations,
      ...scopeBusinesses
        .filter(n => !locations.some(l => l.LocationName === n))
        .map(n => ({ LocationName: n }))
    ];
    return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              helperText={editDialogOpen && selectedUser?.email && formData.email !== selectedUser.email
                ? "Changing email will require user to set a new password"
                : ""}
              error={editDialogOpen && selectedUser?.email && formData.email !== selectedUser.email}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  const updatedFormData = { ...formData, role: newRole };
                  
                  // Clear parent user if the new role doesn't need one
                  if (newRole === 'admin' || newRole === 'agent') {
                    updatedFormData.parentUserId = null;
                  }
                  
                  setFormData(updatedFormData);
                }}
                label="Role"
              >
                <MenuItem value="merchant">Merchant</MenuItem>
                <MenuItem value="subagent">SubAgent</MenuItem>
                {isAdmin && <MenuItem value="agent">Agent</MenuItem>}
                {isAdmin && <MenuItem value="admin">Admin</MenuItem>}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Parent User Selection */}
          {(formData.role === 'subagent' || formData.role === 'merchant') && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Parent User</InputLabel>
                <Select
                  value={formData.parentUserId || ''}
                  onChange={(e) => setFormData({ ...formData, parentUserId: e.target.value })}
                  label="Parent User"
                >
                  <MenuItem value="">Select Parent User</MenuItem>
                  {getAvailableParentUsers().map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.role})
                    </MenuItem>
                  ))}
                </Select>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formData.role === 'subagent' 
                      ? 'SubAgents can be assigned to Admin or Agent users'
                      : 'Merchants can be assigned to Admin, Agent, or SubAgent users'
                    }
                  </Typography>
                </Box>
              </FormControl>
            </Grid>
          )}
          
          {/* Channel Assignment (brand tier; internally "organizations") - Grant access to ALL
              locations within the selected channels. UI canon since 2026-07-07: Channel (brand)
              → Market → Store; "Org" is reserved for a future parent-company tier. */}
          {(formData.role === 'agent' || formData.role === 'subagent' || formData.role === 'merchant') && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={organizations}
                getOptionLabel={(option) => option.name || ''}
                value={organizations.filter(org => org.name && formData.allowedOrganizations.includes(org.name))}
                onChange={(event, newValue) => {
                  // Managers: preserve admin-granted values outside their visible
                  // option set (backend also preserves them server-side)
                  const preserved = isAdmin ? [] : formData.allowedOrganizations.filter(
                    name => !organizations.some(org => org.name === name)
                  );
                  setFormData({
                    ...formData,
                    allowedOrganizations: [...newValue.map(org => org.name), ...preserved]
                  });
                }}
                loading={loadingOrganizations}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Channel Assignment"
                    placeholder="Search and select channels..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingOrganizations ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <ListItemText
                      primary={option.name}
                      secondary={option.channel_count !== undefined || option.store_count !== undefined
                        ? `${option.channel_count || 0} channels • ${option.store_count || 0} stores`
                        : undefined}
                    />
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.name}
                      label={option.name}
                      size="small"
                      sx={{
                        backgroundColor: '#fff3e0',
                        borderColor: '#ff9800',
                        color: '#e65100',
                        '& .MuiChip-deleteIcon': {
                          color: '#ff9800',
                          '&:hover': {
                            color: '#e65100'
                          }
                        }
                      }}
                      variant="outlined"
                    />
                  ))
                }
                isOptionEqualToValue={(option, value) => option.name === value.name}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.name && option.name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                noOptionsText={loadingOrganizations ? "Loading channels..." : "No channels found"}
                disabled={loadingOrganizations}
              />
              <Box sx={{ mt: 1 }}>
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    <strong>Auto-Grant:</strong> Assigning a channel grants access to ALL locations/terminals within that channel.
                    Access updates automatically when stores are added/removed from the channel.
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          )}

          {/* Channel Assignment - HIDDEN (2026-07-07). location_hierarchy.org == .organization
              (channel) for every org except the embryonic Dreams→Clouds split, so this picker
              duplicated the Organization picker name-for-name and an Org grant resolves to the
              same terminals. The channel grant machinery (user_channel_permissions, authorizer,
              consumers) remains fully wired — flip SHOW_CHANNEL_PICKER back to true when parent
              orgs become real (e.g. GTI → Rise/RYTHM) and the two tiers diverge. Existing
              out-of-band channel grants still load and round-trip unchanged on save. */}
          {SHOW_CHANNEL_PICKER && (formData.role === 'agent' || formData.role === 'subagent' || formData.role === 'merchant') && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={distinctChannels}
                getOptionLabel={(option) => option.name || ''}
                value={distinctChannels.filter(ch => ch.name && formData.allowedChannels.includes(ch.name))}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    allowedChannels: newValue.map(ch => ch.name)
                  });
                }}
                loading={loadingChannels}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Channel Assignment"
                    placeholder="Search and select channels..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingChannels ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <ListItemText
                      primary={option.name}
                      secondary="Channel"
                    />
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.name}
                      label={option.name}
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        borderColor: '#1976d2',
                        color: '#0d47a1',
                        '& .MuiChip-deleteIcon': {
                          color: '#1976d2',
                          '&:hover': {
                            color: '#0d47a1'
                          }
                        }
                      }}
                      variant="outlined"
                    />
                  ))
                }
                isOptionEqualToValue={(option, value) => option.name === value.name}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.name && option.name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                noOptionsText={loadingChannels ? "Loading channels..." : "No channels found"}
                disabled={loadingChannels}
              />
              <Box sx={{ mt: 1 }}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    <strong>Channel Grant:</strong> Grants access to all stores within the selected channel(s).
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          )}

          {/* Market Assignment - Grant access to ALL stores within selected Markets */}
          {(formData.role === 'agent' || formData.role === 'subagent' || formData.role === 'merchant') && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={markets}
                getOptionLabel={(option) => option || ''}
                value={formData.allowedMarkets.filter(m => markets.includes(m))}
                onChange={(event, newValue) => {
                  // Managers only see in-scope options; values outside their scope
                  // (admin-granted) must survive the edit or the diff-save would
                  // issue out-of-scope revokes that 403
                  const preserved = isAdmin ? [] : formData.allowedMarkets.filter(m => !markets.includes(m));
                  setFormData({
                    ...formData,
                    allowedMarkets: [...newValue, ...preserved]
                  });
                }}
                loading={loadingChannels}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Market Assignment"
                    placeholder="Search and select markets..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingChannels ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <ListItemText primary={option} />
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{
                        backgroundColor: '#f3e5f5',
                        borderColor: '#9c27b0',
                        color: '#6a1b9a',
                        '& .MuiChip-deleteIcon': {
                          color: '#9c27b0',
                          '&:hover': {
                            color: '#6a1b9a'
                          }
                        }
                      }}
                      variant="outlined"
                    />
                  ))
                }
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option && option.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                noOptionsText={loadingChannels ? "Loading markets..." : "No markets found"}
                disabled={loadingChannels}
              />
              <Box sx={{ mt: 1 }}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    <strong>Market Grant:</strong> Grants access to all stores within the selected market(s).
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          )}

          {/* Location Assignment */}
          {(formData.role === 'agent' || formData.role === 'subagent' || formData.role === 'merchant') && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={locationOptions}
                getOptionLabel={(option) => option.LocationName || ''}
                value={locationOptions.filter(location => formData.allowedLocations.includes(location.LocationName))}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    allowedLocations: newValue.map(location => location.LocationName)
                  });
                }}
                loading={loadingLocations}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Locations (Direct)"
                    placeholder="Search and select locations..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingLocations ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <ListItemText
                      primary={option.LocationName}
                      secondary={option.TerminalIDs ? `${option.TerminalIDs.length} terminals` : 'No terminals'}
                    />
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.LocationName}
                      label={option.LocationName}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                }
                isOptionEqualToValue={(option, value) => option.LocationName === value.LocationName}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.LocationName.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                noOptionsText={loadingLocations ? "Loading locations..." : "No locations found"}
                disabled={loadingLocations}
              />
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {formData.role === 'agent'
                    ? 'Direct location assignments (in addition to channel access above)'
                    : formData.role === 'subagent'
                    ? 'Direct location assignments (in addition to channel access above)'
                    : 'Direct location assignments (in addition to channel access above)'
                  }
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Account Assignment — non-admin managers don't pick accounts: access is
              auto-derived from the assigned store's settlement data (vault + own surcharge) */}
          {!isAdmin && (formData.role === 'agent' || formData.role === 'subagent' || formData.role === 'merchant') && (
            <Grid item xs={12}>
              <Alert severity="info">
                Bank accounts are assigned automatically from the store&apos;s settlement data
                (vault account plus the store&apos;s own surcharge account).
              </Alert>
            </Grid>
          )}
          {isAdmin && (formData.role === 'agent' || formData.role === 'subagent' || formData.role === 'merchant') && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={accounts}
                getOptionLabel={(option) => `${option.accountName} (${option.accountId})`}
                value={accounts.filter(account => formData.allowedAccounts.includes(account.accountId))}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    allowedAccounts: newValue.map(account => account.accountId)
                  });
                }}
                loading={loadingAccounts}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Accounts (Direct)"
                    placeholder="Search and select accounts..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <ListItemText
                      primary={option.accountName}
                      secondary={`Account ID: ${option.accountId}${option.accountNumber ? ` • ${option.accountNumber}` : ''}`}
                    />
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.accountId}
                      label={`${option.accountName} (${option.accountId})`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  ))
                }
                isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.accountName.toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.accountId.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                noOptionsText={loadingAccounts ? "Loading accounts..." : "No accounts found"}
                disabled={loadingAccounts}
              />
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Direct assignments only.
                  {(formData.role === 'agent' || formData.role === 'subagent') && ' Agents and SubAgents automatically see accounts from their descendants.'}
                </Typography>
              </Box>

              {/* Show inherited accounts for agents/subagents in edit mode */}
              {editDialogOpen && selectedUser && (selectedUser.role === 'agent' || selectedUser.role === 'subagent') &&
               selectedUser.inherited_accounts && selectedUser.inherited_accounts.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Inherited from Descendants ({selectedUser.inherited_accounts.length} accounts)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {selectedUser.inherited_accounts.slice(0, 5).map((accountId, index) => {
                      const accountInfo = accounts.find(a => a.accountId === accountId);
                      return (
                        <Chip
                          key={index}
                          label={accountInfo ? `${accountInfo.accountName} (${accountId})` : accountId}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      );
                    })}
                    {selectedUser.inherited_accounts.length > 5 && (
                      <Chip
                        label={`+${selectedUser.inherited_accounts.length - 5} more`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Alert>
              )}
            </Grid>
          )}
          
          {createDialogOpen && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Temporary Password"
                type="password"
                value={formData.temporaryPassword}
                onChange={(e) => setFormData({ ...formData, temporaryPassword: e.target.value })}
                error={!!formData.temporaryPassword && getPasswordErrors(formData.temporaryPassword).length > 0}
                helperText={formData.temporaryPassword ? ' ' : 'Leave empty to auto-generate a compliant password.'}
              />
              {!!formData.temporaryPassword && (
                <Box sx={{ mt: 0.5, ml: 1.5 }}>
                  {[
                    { label: '8+ characters', ok: formData.temporaryPassword.length >= 8 },
                    { label: 'An uppercase letter', ok: /[A-Z]/.test(formData.temporaryPassword) },
                    { label: 'A lowercase letter', ok: /[a-z]/.test(formData.temporaryPassword) },
                    { label: 'A number', ok: /[0-9]/.test(formData.temporaryPassword) },
                    { label: 'A symbol (!@#$…)', ok: /[^A-Za-z0-9]/.test(formData.temporaryPassword) },
                  ].map((r) => (
                    <Typography
                      key={r.label}
                      variant="caption"
                      component="div"
                      sx={{ color: r.ok ? 'success.main' : 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, lineHeight: 1.6 }}
                    >
                      <span style={{ width: 14, display: 'inline-block', textAlign: 'center' }}>{r.ok ? '✓' : '○'}</span>
                      {r.label}
                    </Typography>
                  ))}
                </Box>
              )}
            </Grid>
          )}
          {/* No re-delegation: only admins may grant Can Manage Users */}
          {isAdmin && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.canManageUsers}
                    onChange={(e) => setFormData({ ...formData, canManageUsers: e.target.checked })}
                  />
                }
                label="Can Manage Users"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Permissions and hierarchy settings can be configured after user creation.
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" color="primary">
          {submitText}
        </Button>
      </DialogActions>
    </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Impersonation Banner */}
      {isImpersonating && originalUser && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleStopImpersonation}
              startIcon={<LoginIcon />}
            >
              Stop Impersonation
            </Button>
          }
        >
          <strong>Admin Impersonation Active:</strong> You are viewing as another user. 
          Originally logged in as: {originalUser.firstName} {originalUser.lastName} ({originalUser.email})
        </Alert>
      )}

      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Search Users"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  label="Role"
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                  <MenuItem value="subagent">SubAgent</MenuItem>
                  <MenuItem value="merchant">Merchant</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadUsers}
                fullWidth
                disabled={loading}
              >
                Refresh
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              {isManager && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                  fullWidth
                >
                  Add User
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Paper sx={{ position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Parent</TableCell>
                <TableCell>Locations</TableCell>
                <TableCell>Location Count</TableCell>
                <TableCell>Children</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getRoleIcon(user.role)}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {user.first_name} {user.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.status}
                      color={getStatusColor(user.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.parent_name || 'Root User'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {/* Show Organizations first with orange chips */}
                      {user.allowed_organizations && user.allowed_organizations.length > 0 && (
                        <>
                          {user.allowed_organizations.slice(0, 2).map((org, index) => (
                            <Chip
                              key={`org-${index}`}
                              label={org}
                              size="small"
                              sx={{
                                backgroundColor: '#fff3e0',
                                borderColor: '#ff9800',
                                color: '#e65100'
                              }}
                              variant="outlined"
                              title="Channel - grants access to all locations within"
                            />
                          ))}
                          {user.allowed_organizations.length > 2 && (
                            <Chip
                              label={`+${user.allowed_organizations.length - 2} channels`}
                              size="small"
                              sx={{ borderColor: '#ff9800', color: '#e65100' }}
                              variant="outlined"
                            />
                          )}
                        </>
                      )}
                      {/* Show Locations */}
                      {user.total_businesses && user.total_businesses.length > 0 ? (
                        <>
                          {user.total_businesses.slice(0, user.allowed_organizations?.length > 0 ? 2 : 3).map((location, index) => {
                            const isDirect = user.allowed_locations && user.allowed_locations.includes(location);
                            const isInherited = user.inherited_businesses && user.inherited_businesses.includes(location);
                            return (
                              <Chip
                                key={index}
                                label={location}
                                size="small"
                                variant={isDirect ? "filled" : "outlined"}
                                color={isDirect ? "primary" : "info"}
                                title={isDirect && isInherited ? "Direct + Inherited" : isDirect ? "Direct" : "Inherited from descendants"}
                              />
                            );
                          })}
                        </>
                      ) : !user.allowed_organizations?.length && (
                        <Typography variant="body2" color="text.secondary">
                          {user.role === 'admin' ? 'All' : 'None'}
                        </Typography>
                      )}
                      {user.total_businesses && user.total_businesses.length > (user.allowed_organizations?.length > 0 ? 2 : 3) && (
                        <Chip
                          label={`+${user.total_businesses.length - (user.allowed_organizations?.length > 0 ? 2 : 3)}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={user.total_businesses ? user.total_businesses.length : 0}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      {user.inherited_businesses && user.inherited_businesses.length > 0 && (
                        <Tooltip title={`${user.allowed_locations?.length || 0} direct + ${user.inherited_businesses.length} inherited`}>
                          <Chip
                            label={`↓${user.inherited_businesses.length}`}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.child_count || 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(user.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={user.last_login ? 'textPrimary' : 'textSecondary'}>
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString()
                        : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setMenuUserId(user.id);
                        setSelectedUser(user);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setMenuUserId(null);
        }}
      >
        {canMutateSelected && (
          <MenuItem onClick={() => {
            handleEditClick(selectedUser);
            setAnchorEl(null);
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit User</ListItemText>
          </MenuItem>
        )}

        {currentUser?.role === 'admin' && selectedUser?.role !== 'admin' && selectedUser?.id !== currentUser.userId && (
          <MenuItem onClick={() => {
            handleImpersonateClick(selectedUser);
            setAnchorEl(null);
          }}>
            <ListItemIcon>
              <LoginIcon fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>Login as User</ListItemText>
          </MenuItem>
        )}

        {canMutateSelected && (
          <MenuItem onClick={() => {
            handleResetPasswordClick(selectedUser);
            setAnchorEl(null);
          }}>
            <ListItemIcon>
              <LockResetIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText>Reset Password</ListItemText>
          </MenuItem>
        )}

        {canMutateSelected && (
          <MenuItem onClick={() => {
            handleCopyUserClick(selectedUser);
            setAnchorEl(null);
          }}>
            <ListItemIcon>
              <FileCopyIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Copy User</ListItemText>
          </MenuItem>
        )}

        {canMutateSelected && (
          <MenuItem onClick={() => {
            handleSuspendUser(selectedUser.id, selectedUser.status === 'active');
            setAnchorEl(null);
          }}>
            <ListItemIcon>
              {selectedUser?.status === 'active' ?
                <BlockIcon fontSize="small" color="warning" /> :
                <CheckCircleIcon fontSize="small" color="success" />
              }
            </ListItemIcon>
            <ListItemText>
              {selectedUser?.status === 'active' ? 'Suspend User' : 'Activate User'}
            </ListItemText>
          </MenuItem>
        )}

        {canMutateSelected && (
          <MenuItem onClick={() => {
            handleDeleteClick(selectedUser);
            setAnchorEl(null);
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete User</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create User Dialog */}
      {renderUserFormDialog(
        createDialogOpen,
        () => setCreateDialogOpen(false),
        handleCreateUser,
        'Create New User',
        'Create User'
      )}

      {/* Edit User Dialog */}
      {renderUserFormDialog(
        editDialogOpen,
        () => setEditDialogOpen(false),
        handleEditUser,
        'Edit User',
        'Update User'
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.first_name} {selectedUser?.last_name}"?
            This action cannot be undone.
          </Typography>
          {selectedUser?.child_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This user has {selectedUser.child_count} child user(s). 
              Please reassign or delete child users first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            variant="contained"
            disabled={selectedUser?.child_count > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Impersonation Confirmation Dialog */}
      <Dialog open={impersonateDialogOpen} onClose={() => setImpersonateDialogOpen(false)}>
        <DialogTitle>Login as User</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            You are about to impersonate user "{selectedUser?.first_name} {selectedUser?.last_name}" ({selectedUser?.email}).
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This will allow you to view the application exactly as this user would see it, with their specific permissions and data access.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Security Notice:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>This action will be logged for audit purposes</li>
                <li>You can return to your admin account at any time</li>
                <li>All actions performed while impersonating will be attributed to the impersonated user</li>
                <li>Use this feature responsibly for support and troubleshooting only</li>
              </ul>
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpersonateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleImpersonateUser} 
            color="warning" 
            variant="contained"
            startIcon={<LoginIcon />}
          >
            Login as User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset User Password</DialogTitle>
        <DialogContent>
          {!resetPasswordResult ? (
            <>
              <Typography gutterBottom>
                You are about to reset the password for user "{selectedUser?.first_name} {selectedUser?.last_name}" ({selectedUser?.email}).
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This will generate a temporary password that the user must change on their next login.
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" component="div">
                  <strong>Important:</strong>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>The user's current password will be invalidated</li>
                    <li>A temporary password will be generated</li>
                    <li>The user will be forced to change their password on next login</li>
                    <li>Make sure to securely share the temporary password with the user</li>
                  </ul>
                </Typography>
              </Alert>
            </>
          ) : resetPasswordResult.success ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Password reset successfully!
              </Alert>
              <Typography gutterBottom>
                <strong>User:</strong> {resetPasswordResult.email}
              </Typography>
              <Typography gutterBottom>
                <strong>Temporary Password:</strong>
              </Typography>
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.100', 
                  border: '1px solid', 
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '1.1rem',
                  mb: 2 
                }}
              >
                {resetPasswordResult.temporaryPassword}
              </Box>
              <Alert severity="info">
                <Typography variant="body2">
                  Please securely share this temporary password with the user. 
                  They will be required to change it on their next login.
                </Typography>
              </Alert>
            </>
          ) : (
            <Alert severity="error">
              <Typography variant="body2">
                <strong>Error:</strong> {resetPasswordResult.error}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>
            {resetPasswordResult ? 'Close' : 'Cancel'}
          </Button>
          {!resetPasswordResult && (
            <Button 
              onClick={handleResetPassword} 
              color="warning" 
              variant="contained"
              startIcon={<LockResetIcon />}
            >
              Reset Password
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Copy User Dialog */}
      <Dialog open={copyUserDialogOpen} onClose={() => setCopyUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Copy User</DialogTitle>
        <DialogContent>
          {/* Source User Info */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Copying from:</strong> {selectedUser?.first_name} {selectedUser?.last_name} ({selectedUser?.email})
            </Typography>
            <Typography variant="caption" component="div">
              The new user will inherit:
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Role: {selectedUser?.role}</li>
                <li>Parent User: {selectedUser?.parent_name || 'None'}</li>
                <li>Channels: {selectedUser?.allowed_organizations?.length || 0}</li>
                <li>Locations: {selectedUser?.allowed_locations?.length || 0}</li>
                <li>Terminals: {selectedUser?.allowed_terminals?.length || 0}</li>
                <li>Accounts: {selectedUser?.allowed_accounts?.length || 0}</li>
                <li>User Management Permission: {selectedUser?.can_manage_users ? 'Yes' : 'No'}</li>
              </ul>
            </Typography>
          </Alert>

          {/* New User Form */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email (Required)"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                helperText="Enter the email address for the new user"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                helperText="Defaults to source user's name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                helperText="Defaults to source user's name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Temporary Password (Optional)"
                type="password"
                value={formData.temporaryPassword}
                onChange={(e) => setFormData({ ...formData, temporaryPassword: e.target.value })}
                helperText="Leave empty to auto-generate a secure password"
              />
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> All permissions, role settings, and configurations will be copied from the source user.
              You can edit these after the user is created.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyUserDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCopyUser}
            variant="contained"
            color="primary"
            startIcon={<FileCopyIcon />}
            disabled={!formData.email}
          >
            Copy User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;